import type { JSX } from 'react'

import {
  Button,
  Flex,
  ImagePlusIcon,
  Text,
  TrashIcon,
} from '@buffer-mono/popcorn'

import { AssetThumbnail } from '~publish/components/AssetThumbnail'
import { MediaAttachmentTray } from '~publish/components/Composer/components/MediaAttachmentTray'
import { FileInput } from '~publish/components/FileInput'
import { GuestMediaAssetContent } from '~publish/components/GuestMedia'
import { UploadButton } from '~publish/components/UploadButton'
import type { GuestMedia } from '~publish/helpers/guestMedia'
import {
  GUEST_MEDIA_ACCEPT,
  GUEST_MEDIA_MAX_COUNT,
  isGuestMediaExpired,
} from '~publish/helpers/guestMedia'
import type { GuestMediaAccessController } from '~publish/hooks/useGuestMediaAccess'
import {
  AltToolButton,
  EditToolButton,
  TagToolButton,
} from '~publish/legacy/composer/composer/components/media-attachment-thumbnail/thumbnail-tools'

import styles from './PublicComposerPage.module.css'
import type { GuestImageWorkspaceTask } from './PublicGuestImageWorkspace'

function getSelectedFiles(
  event: React.ChangeEvent<HTMLInputElement>,
): readonly File[] {
  return Array.from(event.target.files ?? [])
}

function isExpiredGuestMedia(
  media: GuestMedia,
  access: GuestMediaAccessController,
): boolean {
  return (
    isGuestMediaExpired(media) ||
    access.states.get(media.assetUrl)?.status === 'expired'
  )
}

function ExpiredGuestMediaTile({
  media,
  access,
  disabled,
  replacing,
  onReplace,
  onRemove,
}: {
  media: GuestMedia
  access: GuestMediaAccessController
  disabled: boolean
  replacing: boolean
  onReplace: (mediaId: string, files: readonly File[]) => void
  onRemove: (mediaId: string) => void
}): JSX.Element {
  return (
    <div className={styles.expiredMediaTile}>
      <GuestMediaAssetContent
        media={media}
        access={access}
        className={styles.expiredMediaPreview}
      />
      <Text className={styles.expiredMediaName} size="sm">
        {media.fileName}
      </Text>
      <Flex gap="xs" justify="center">
        <FileInput
          accept={GUEST_MEDIA_ACCEPT}
          disabled={disabled}
          onChange={(event): void =>
            onReplace(media.id, getSelectedFiles(event))
          }
        >
          <Button size="small" variant="secondary" disabled={disabled}>
            <ImagePlusIcon />
            {replacing ? 'Uploading' : 'Upload replacement'}
          </Button>
        </FileInput>
        <Button
          size="small"
          variant="tertiary"
          disabled={disabled}
          onClick={(): void => onRemove(media.id)}
        >
          <TrashIcon /> Remove
        </Button>
      </Flex>
    </div>
  )
}

function ActiveGuestMediaTile({
  media,
  access,
  disabled,
  isEditing,
  canTagPeople,
  onEdit,
  onRequestTaggingSignup,
  onRemove,
}: {
  media: GuestMedia
  access: GuestMediaAccessController
  disabled: boolean
  isEditing: boolean
  canTagPeople: boolean
  onEdit: (
    media: GuestMedia,
    trigger: HTMLButtonElement,
    task: GuestImageWorkspaceTask,
  ) => void
  onRequestTaggingSignup: (trigger: HTMLButtonElement) => void
  onRemove: (mediaId: string) => void
}): JSX.Element {
  const isGif = media.mimeType === 'image/gif'
  const content = <GuestMediaAssetContent media={media} access={access} />

  return (
    <AssetThumbnail className={styles.mediaThumbnail}>
      {!isGif && access.states.get(media.assetUrl)?.status === 'ready' ? (
        <AssetThumbnail.Clickable
          aria-label="Select to edit image"
          aria-expanded={isEditing}
          aria-haspopup="dialog"
          disabled={disabled}
          onClick={(event): void => onEdit(media, event.currentTarget, 'edit')}
        >
          {content}
        </AssetThumbnail.Clickable>
      ) : (
        content
      )}
      {isGif ? (
        <AssetThumbnail.Indicator type="gif" />
      ) : (
        <AssetThumbnail.Actions>
          {canTagPeople ? (
            <TagToolButton
              disabled={disabled}
              label={`Tag people in ${media.fileName}`}
              onClick={(event): void =>
                onRequestTaggingSignup(event.currentTarget)
              }
            />
          ) : null}
          <AltToolButton
            disabled={disabled}
            altText={media.altText ?? null}
            onClick={(event): void =>
              onEdit(media, event.currentTarget, 'alt-text')
            }
          />
          <EditToolButton
            disabled={disabled}
            label={`Edit ${media.fileName}`}
            onClick={(event): void =>
              onEdit(media, event.currentTarget, 'edit')
            }
          />
        </AssetThumbnail.Actions>
      )}
      <AssetThumbnail.Close
        disabled={disabled}
        label={`Remove ${media.fileName}`}
        tooltip="Remove image"
        onClick={(): void => onRemove(media.id)}
      />
    </AssetThumbnail>
  )
}

export function PublicGuestMediaEditor({
  media,
  access,
  pendingUploadCount,
  pendingUploadProgress,
  replacingMediaIds,
  editingMediaId,
  disabled,
  canTagPeople,
  onAddFiles,
  onEdit,
  onRequestTaggingSignup,
  onReplace,
  onRemove,
}: {
  media: readonly GuestMedia[]
  access: GuestMediaAccessController
  pendingUploadCount: number
  pendingUploadProgress: number
  replacingMediaIds: ReadonlySet<string>
  editingMediaId?: string
  disabled: boolean
  canTagPeople: boolean
  onAddFiles: (files: readonly File[]) => void
  onEdit: (
    media: GuestMedia,
    trigger: HTMLButtonElement,
    task: GuestImageWorkspaceTask,
  ) => void
  onRequestTaggingSignup: (trigger: HTMLButtonElement) => void
  onReplace: (mediaId: string, files: readonly File[]) => void
  onRemove: (mediaId: string) => void
}): JSX.Element {
  const hasReachedLimit = media.length >= GUEST_MEDIA_MAX_COUNT

  return (
    <div className={styles.mediaEditor}>
      <MediaAttachmentTray>
        {media.map((asset) =>
          isExpiredGuestMedia(asset, access) ? (
            <ExpiredGuestMediaTile
              key={asset.id}
              media={asset}
              access={access}
              disabled={disabled}
              replacing={replacingMediaIds.has(asset.id)}
              onReplace={onReplace}
              onRemove={onRemove}
            />
          ) : (
            <ActiveGuestMediaTile
              key={asset.id}
              media={asset}
              access={access}
              disabled={disabled}
              isEditing={asset.id === editingMediaId}
              canTagPeople={canTagPeople}
              onEdit={onEdit}
              onRequestTaggingSignup={onRequestTaggingSignup}
              onRemove={onRemove}
            />
          ),
        )}
        <UploadButton
          accept={GUEST_MEDIA_ACCEPT}
          busy={pendingUploadCount > 0}
          progress={pendingUploadProgress}
          pendingCount={pendingUploadCount}
          multiple
          disabled={disabled || hasReachedLimit}
          onFileChange={onAddFiles}
        />
      </MediaAttachmentTray>
      {hasReachedLimit ? (
        <Text size="sm" color="subtle" className={styles.mediaMeta}>
          Remove an image before you add another.
        </Text>
      ) : null}
    </div>
  )
}
